import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight, Calendar, Tag, User } from "lucide-react";
import axios from "axios";
import { PageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function getCategoryColor(cat) {
  const colors = {
    "AI & Technology": "text-[#38BDF8] border-[#38BDF8]/30",
    "Industry Insights": "text-[#F472B6] border-[#F472B6]/30",
    "Streaming & Distribution": "text-[#A78BFA] border-[#A78BFA]/30",
    "Indie Filmmaking": "text-[#34D399] border-[#34D399]/30",
    "Creative Process": "text-[#FBBF24] border-[#FBBF24]/30",
    "Culture & Trends": "text-[#FB923C] border-[#FB923C]/30",
  };
  return colors[cat] || "text-[#71717A] border-[#71717A]/30";
}

export default function ThoughtDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/thoughts/${slug}`);
        setPost(res.data);
      } catch (e) {
        console.error("Failed to fetch post", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-6 h-6 border border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#050505]">
        <PageSEO title="Post Not Found" description="The requested post could not be found." path={`/thoughts/${slug}`} />
        <p className="text-[#52525B] text-sm">Post not found</p>
        <Link to="/thoughts" className="text-xs text-[#38BDF8] hover:underline uppercase tracking-wider">Back to Thoughts</Link>
      </div>
    );
  }

  const paragraphs = post.content.split(/\n\n+/).filter(Boolean);

  return (
    <div data-testid="thought-detail-page" className="min-h-screen bg-[#050505]">
      <PageSEO
        title={post.title}
        description={post.excerpt || post.content.substring(0, 155)}
        path={`/thoughts/${post.slug}`}
        type="article"
      />

      <div className="pt-36 pb-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-[720px] mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-10">
              <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
                <li><Link to="/" className="text-[#3f3f46] hover:text-white transition-colors">Home</Link></li>
                <li><ChevronRight size={10} className="text-[#27272A]" /></li>
                <li><Link to="/thoughts" className="text-[#3f3f46] hover:text-white transition-colors">Thoughts</Link></li>
                <li><ChevronRight size={10} className="text-[#27272A]" /></li>
                <li className="text-[#71717A] truncate max-w-[200px]">{post.title}</li>
              </ol>
            </nav>

            {/* Category & Date */}
            <div className="flex items-center gap-3 mb-6">
              <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 border rounded-none ${getCategoryColor(post.category)}`}>
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-['Syne'] text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.03em] text-white mb-8 leading-[1.1]">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 mb-12 pb-10 border-b border-[#1A1A1A]">
              <div className="flex items-center gap-2 text-sm text-[#71717A]">
                <Calendar size={13} />
                <span>{formatDate(post.published_date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#71717A]">
                <User size={13} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#71717A]">
                <Tag size={13} />
                <span>{post.category}</span>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              {paragraphs.map((para, i) => (
                <p key={i} className="text-base text-[#A1A1AA] leading-[1.85] font-light">
                  {para}
                </p>
              ))}
            </div>

            {/* Back link */}
            <div className="mt-16 pt-10 border-t border-[#1A1A1A]">
              <Link
                to="/thoughts"
                data-testid="back-to-thoughts"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B] hover:text-white transition-colors"
              >
                <ArrowLeft size={12} /> Back to All Thoughts
              </Link>
            </div>
          </motion.article>
        </div>
      </div>
    </div>
  );
}
